/**
 * Measure Selection Store
 *
 * Drives the client-side per-measure Griffschrift assembly:
 *  1. loadVariants()  -> POST /api/convert/variants : fetch every variant of every
 *     measure (renderable MusicXML fragments) plus a recommended default selection.
 *  2. The user freely picks/changes a variant per measure (purely local, no roundtrip).
 *  3. assemble()      -> POST /api/convert/assemble : build + export the final score
 *     from the chosen selection and hand the result to the upload store so the
 *     existing preview/download pipeline shows it.
 *
 * Settings used for variant generation are frozen and re-sent on assemble so the
 * deterministic re-computation produces the exact same variants the user picked from.
 */
import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import {
    type ConversionSettings,
    type ConvertVariantsResponse,
    type MeasureVariants,
} from '@/generated';
import {apiClient, extractApiErrorMessage, isAbortError} from '@/api-client';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useSettingsStore} from '@/stores/settings';
import {pinConversionSettings} from '@/stores/settingsPins';
import {useNotificationStore} from '@/stores/notifications';
import {createRequestRegistry} from '@/shared/requestAbort';

export const useMeasureSelectionStore = defineStore('measureSelection', () => {
    const uploadStore = useMusicUploadStore();
    const settingsStore = useSettingsStore();
    const notificationStore = useNotificationStore();

    const variantsResponse = ref<ConvertVariantsResponse | null>(null);
    const selection = ref<string[]>([]);
    const isLoadingVariants = ref(false);
    const isAssembling = ref(false);

    // Frozen at variant-generation time – assemble must use the identical settings UND den
    // identischen MusicXML-Inhalt, damit die deterministische Neuberechnung exakt dieselben
    // internen Variantennamen liefert (inhaltsbasiert).
    let frozenSettings: ConversionSettings | null = null;
    let frozenMusicXml = '';

    // Abbrechbare Requests: ein neuer Aufruf derselben Aktion bricht den Vorgänger ab.
    const requests = createRequestRegistry();
    const beginRequest = requests.begin;

    const measures = computed<MeasureVariants[]>(() => variantsResponse.value?.measures ?? []);
    const numMeasures = computed(() => variantsResponse.value?.num_measures ?? 0);
    const variantLabels = computed<Record<string, string>>(() => variantsResponse.value?.variant_labels ?? {});
    const defaultSelection = computed<string[]>(() => variantsResponse.value?.default_selection ?? []);
    const hasVariants = computed(() => measures.value.length > 0);

    function buildSettings(): ConversionSettings {
        // Pin wie im Auto-/Global-Pfad: Konvertierung überschreibt nie Original-Akkorde.
        return pinConversionSettings({...settingsStore.settings} as ConversionSettings);
    }

    async function loadVariants(): Promise<boolean> {
        const fileId = uploadStore.currentFileId;
        if (!fileId) {
            notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning');
            return false;
        }

        isLoadingVariants.value = true;
        variantsResponse.value = null;
        selection.value = [];

        const settings = buildSettings();
        const musicXml = uploadStore.selectedFileContent ?? '';

        try {
            const data = await apiClient.convertVariants({
                fileId,
                musicXml,
                settings,
            }, {signal: beginRequest('variants')});
            variantsResponse.value = data;
            selection.value = [...(data.default_selection ?? [])];
            frozenSettings = settings;
            frozenMusicXml = musicXml;
            return true;
        } catch (error) {
            if (!isAbortError(error)) {
                notificationStore.notify(
                    extractApiErrorMessage(error, 'Variantengenerierung fehlgeschlagen'), 'error',
                );
            }
            return false;
        } finally {
            isLoadingVariants.value = false;
        }
    }

    function setVariant(measureIndex: number, variantName: string): void {
        if (measureIndex < 0 || measureIndex >= selection.value.length) return;
        const next = [...selection.value];
        next[measureIndex] = variantName;
        selection.value = next;
    }

    function resetToDefault(): void {
        selection.value = [...defaultSelection.value];
    }

    function resetMeasure(measureIndex: number): void {
        const def = defaultSelection.value[measureIndex];
        if (def) setVariant(measureIndex, def);
    }

    async function assemble(): Promise<boolean> {
        const fileId = uploadStore.currentFileId;
        if (!fileId || !variantsResponse.value) return false;

        isAssembling.value = true;
        try {
            const usedSettings = frozenSettings ?? buildSettings();
            const usedMusicXml = frozenMusicXml || (uploadStore.selectedFileContent ?? '');
            const res = await apiClient.convertAssemble({
                fileId,
                musicXml: usedMusicXml,
                settings: usedSettings,
                perMeasureSelection: [...selection.value],
            }, {signal: beginRequest('assemble')});
            // Feed the assembled result into the existing preview/download pipeline. Die
            // eingefrorenen Settings reisen mit ins 'converted'-Capture-Event (Snapshot); der
            // eingefrorene INHALT speist `isConversionStale` – hat sich die Eingabe seit
            // `loadVariants` geändert, ist das Ergebnis damit sofort korrekt als veraltet markiert.
            uploadStore.setConversionResult(res ?? null, usedSettings, usedMusicXml);
            notificationStore.notify('Griffschrift zusammengesetzt.', 'success', 2000);
            return true;
        } catch (error) {
            if (!isAbortError(error)) {
                notificationStore.notify(
                    extractApiErrorMessage(error, 'Zusammensetzen fehlgeschlagen'), 'error',
                );
            }
            return false;
        } finally {
            isAssembling.value = false;
        }
    }

    function reset(): void {
        requests.abortAll();
        variantsResponse.value = null;
        selection.value = [];
        isLoadingVariants.value = false;
        isAssembling.value = false;
        frozenSettings = null;
        frozenMusicXml = '';
    }

    // Bei Datei-/Lineage-Wechsel (frischer Upload, Reuse, Movement-Wechsel – und upload.reset(),
    // das die lineageId leert) sind Varianten, Auswahl und der eingefrorene Inhalt der ALTEN
    // Datei bedeutungslos und dürfen nicht in die neue hineinragen. Bewusst an der lineageId,
    // nicht an jedem Edit: innerhalb einer Linie bleibt der eingefrorene Stand die dokumentierte
    // Determinismus-Basis für assemble. Test: measureSelectionReset.spec.ts.
    watch(() => uploadStore.lineageId, () => reset());

    return {
        variantsResponse,
        selection,
        isLoadingVariants,
        isAssembling,
        measures,
        numMeasures,
        variantLabels,
        defaultSelection,
        hasVariants,
        loadVariants,
        setVariant,
        resetToDefault,
        resetMeasure,
        assemble,
        reset,
    } as const;
});
